import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Bell, ShieldAlert, BellRing, Info, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotifications, AppNotification } from "@/hooks/useNotifications";
import { motion, AnimatePresence } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

let globalLastToastedId: string | null = null;

export function NotificationBell() {
  const { notifications, markAsViewed } = useNotifications();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const unreadCount = notifications.length;

  // Handle popups directly inside the visual component that holds the DOM context
  useEffect(() => {
    if (notifications.length === 0) return;

    try {
      const notifiedIdsStr = localStorage.getItem('notified_ids') || '[]';
      const notifiedIds: string[] = JSON.parse(notifiedIdsStr);
      let updated = false;

      // Find completely new notifications
      const newNotifications = notifications.filter(notif => !notifiedIds.includes(notif.id));

      if (newNotifications.length > 0) {
        // Use the ID of the first (newest) notification as the global lock for the entire batch
        const batchLockId = newNotifications[0].id;
        
        if (batchLockId !== globalLastToastedId) {
          globalLastToastedId = batchLockId;

          setTimeout(() => {
            if (newNotifications.length === 1) {
              const notif = newNotifications[0];
              toast({
                title: notif.severity === 'severe' ? "Urgent Alert" : "New Notification",
                description: notif.message,
                variant: notif.severity === 'severe' ? "destructive" : "default",
                duration: 30000,
              });
            } else {
              toast({
                title: "New Notifications",
                description: `Hey VasisPro user you have ${newNotifications.length} new notifications, please visit the notification center.`,
                variant: "default",
                duration: 30000,
              });
            }
          }, 100);

          newNotifications.forEach(notif => {
            notifiedIds.push(notif.id);
          });
          
          updated = true;
        }
      }

      if (updated) {
        if (notifiedIds.length > 100) {
          notifiedIds.splice(0, notifiedIds.length - 100);
        }
        localStorage.setItem('notified_ids', JSON.stringify(notifiedIds));
      }
    } catch (e) {
      console.error("Error managing notification popups:", e);
    }
  }, [notifications, toast]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-10 w-10 shrink-0 rounded-full hover:bg-slate-100/50">
          <Bell className="h-5 w-5 text-slate-600" />
          {unreadCount > 0 && (
            <span className="absolute top-2 right-2 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-600"></span>
            </span>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent 
        align="end" 
        className="w-[380px] p-0 rounded-3xl border border-slate-200/50 backdrop-blur-md bg-white/80 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-semibold tracking-tight text-slate-800">Notifications</h3>
          {unreadCount > 0 && (
            <span className="text-xs font-medium bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
              {unreadCount} new
            </span>
          )}
        </div>

        <ScrollArea className="h-[400px]">
          <div className="p-4 space-y-4">
            <AnimatePresence initial={false}>
              {notifications.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center py-12 text-center"
                >
                  <div className="h-12 w-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
                    <Check className="h-6 w-6 text-slate-300" />
                  </div>
                  <p className="text-sm font-medium text-slate-600">You're all caught up</p>
                  <p className="text-xs text-slate-400 mt-1">No new notifications</p>
                </motion.div>
              ) : (
                notifications.map((notif: AppNotification) => (
                  <NotificationItem 
                    key={notif.id} 
                    notif={notif} 
                    onView={() => markAsViewed(notif.id)} 
                  />
                ))
              )}
            </AnimatePresence>
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

function NotificationItem({ notif, onView }: { notif: AppNotification, onView: () => void }) {
  const isSevere = notif.severity === 'severe';
  const isMedium = notif.severity === 'medium';
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95, height: 0, marginBottom: 0, overflow: 'hidden' }}
      transition={{ duration: 0.2 }}
      className={cn(
        "group flex gap-4 p-4 rounded-2xl transition-colors border",
        isSevere 
          ? "bg-red-50/30 border-red-100/50 hover:bg-red-50/50" 
          : "bg-slate-50/50 border-slate-100/50 hover:bg-slate-50"
      )}
    >
      <div className="shrink-0 mt-1">
        {isSevere ? (
          <ShieldAlert className="h-5 w-5 text-red-500" />
        ) : isMedium ? (
          <BellRing className="h-5 w-5 text-amber-500" />
        ) : (
          <Info className="h-5 w-5 text-blue-500" />
        )}
      </div>
      
      <div className="flex-1 space-y-1">
        <p className={cn(
          "text-sm leading-snug break-words",
          isSevere ? "text-red-900 font-medium" : "text-slate-700"
        )}>
          {notif.message}
        </p>
        <p className="text-xs text-slate-400 font-medium">
          {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
        </p>
      </div>

      <div className="shrink-0 flex items-center justify-center">
        <label 
          className="flex items-center justify-center h-8 w-8 rounded-full hover:bg-slate-200/50 cursor-pointer transition-colors"
          title="Mark as viewed"
        >
          <Checkbox 
            className={cn(
              "h-5 w-5 rounded-md border-slate-300 transition-all",
              "data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
            )}
            onCheckedChange={(checked) => {
              if (checked) {
                // adding brief timeout to allow check animation
                setTimeout(() => onView(), 300);
              }
            }} 
          />
        </label>
      </div>
    </motion.div>
  );
}
